import { useEffect, useRef, useState } from 'react'

// usePrefersReducedMotion respects the OS "reduce motion" setting so every
// delight here degrades to an instant, static render for those who ask for it.
export const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return reduced
}

// useCountUp animates a number from 0 to target with an ease-out curve. When
// disabled (reduced motion), it returns the target immediately.
export const useCountUp = (target: number, enabled: boolean, durationMs = 900): number => {
  const [value, setValue] = useState(enabled ? 0 : target)
  const rafRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (!enabled) {
      setValue(target)
      return
    }
    let start: number | null = null
    const tick = (t: number) => {
      if (start === null) start = t
      const p = Math.min(1, (t - start) / durationMs)
      const eased = 1 - Math.pow(1 - p, 3) // easeOutCubic
      setValue(target * eased)
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        setValue(target)
      }
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [target, enabled, durationMs])

  return value
}

// useMounted flips to true one frame after mount — used to drive CSS-transition
// "grow from zero" reveals (ring fills, progress bars) without a library.
export const useMounted = (): boolean => {
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [])
  return mounted
}
